import {
  IMPROMPTU_PRONUNCIATION_VERSION,
  IMPROMPTU_RUBRIC_VERSION,
  IMPROMPTU_SCHEMA_VERSION,
} from "@/lib/impromptu/config";
import { z } from "zod";

export const SonioxTokenSchema = z
  .object({
    text: z.string(),
    start_ms: z.number().nonnegative().optional(),
    end_ms: z.number().nonnegative().optional(),
    confidence: z.number().min(0).max(1),
    language: z.string().nullable().optional(),
    is_audio_event: z.boolean().nullable().optional(),
    translation_status: z.enum(["none", "original", "translation"]).nullable().optional(),
  })
  .passthrough();

export type SonioxToken = z.infer<typeof SonioxTokenSchema>;

export const TranscriptWordSchema = z
  .object({
    text: z.string().min(1).max(120),
    normalized: z.string().min(1).max(120),
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().nonnegative(),
    // Gemini Transcribe supplies word timestamps but not recognition confidence.
    confidence: z.number().min(0).max(1).nullable(),
    language: z.string().nullable(),
    lowConfidence: z.boolean(),
  })
  .strict();

export type TranscriptWord = z.infer<typeof TranscriptWordSchema>;

export const PauseSchema = z
  .object({
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().nonnegative(),
    durationMs: z.number().int().nonnegative(),
    kind: z.enum(["natural", "long"]),
  })
  .strict();

export const FillerCandidateSchema = z
  .object({
    word: z.string().min(1).max(40),
    count: z.number().int().positive().max(100),
    occurrences: z
      .array(
        z
          .object({
            startMs: z.number().int().nonnegative(),
            endMs: z.number().int().nonnegative(),
          })
          .strict()
      )
      .max(12),
  })
  .strict();

export const RepeatedWordSchema = z
  .object({
    word: z.string().min(1).max(60),
    count: z.number().int().min(2).max(100),
  })
  .strict();

export const DeliveryMetricsSchema = z
  .object({
    durationMs: z.number().int().nonnegative(),
    speakingSpanMs: z.number().int().nonnegative(),
    activeSpeakingMs: z.number().int().nonnegative(),
    wordCount: z.number().int().nonnegative(),
    wordsPerMinute: z.number().nonnegative().max(500).nullable(),
    paceBand: z.enum(["measured", "steady", "quick", "insufficient"]),
    pauseCount: z.number().int().nonnegative(),
    longPauseCount: z.number().int().nonnegative(),
    pauses: z.array(PauseSchema).max(60),
    fillerCandidates: z.array(FillerCandidateSchema).max(8),
    repeatedWords: z.array(RepeatedWordSchema).max(8),
    lowConfidenceWordCount: z.number().int().nonnegative(),
    evidenceQuality: z.enum(["good", "mixed", "insufficient"]),
  })
  .strict();

export type DeliveryMetrics = z.infer<typeof DeliveryMetricsSchema>;

const EvidenceStatusSchema = z.enum(["supported", "insufficient"]);

export const ModelObservationSchema = z
  .object({
    title: z.string().min(1).max(90),
    feedback: z.string().min(1).max(320),
    evidenceStatus: EvidenceStatusSchema,
    evidenceQuote: z.string().min(1).max(180).nullable(),
  })
  .strict();

const DimensionBandSchema = z.enum(["strong", "developing", "needs_focus", "insufficient"]);

export const ModelDimensionSchema = z
  .object({
    score: z.number().int().min(0).max(100).nullable(),
    feedback: z.string().min(1).max(320),
    evidenceStatus: EvidenceStatusSchema,
    evidenceQuote: z.string().min(1).max(180).nullable(),
  })
  .strict();

export const GeminiImpromptuAnalysisSchema = z
  .object({
    headline: z.string().min(1).max(140),
    keepDoing: ModelObservationSchema,
    tryNext: ModelObservationSchema,
    message: z
      .object({
        relevance: ModelDimensionSchema,
        structure: ModelDimensionSchema,
      })
      .strict(),
    language: z
      .object({
        grammar: ModelDimensionSchema,
        vocabulary: ModelDimensionSchema,
      })
      .strict(),
    preciseWording: z.array(z.string().min(1).max(100)).max(3),
    sharperVersion: z.string().min(1).max(900),
  })
  .strict();

export type GeminiImpromptuAnalysis = z.infer<typeof GeminiImpromptuAnalysisSchema>;

export const EvidenceReferenceSchema = z
  .object({
    quote: z.string().min(1).max(180),
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().nonnegative(),
  })
  .strict();

export const ReportObservationSchema = ModelObservationSchema.omit({ evidenceQuote: true }).extend({
  evidence: EvidenceReferenceSchema.nullable(),
});

export const ReportDimensionSchema = ModelDimensionSchema.omit({ evidenceQuote: true }).extend({
  band: DimensionBandSchema,
  evidence: EvidenceReferenceSchema.nullable(),
});

export const ImpromptuReportSchema = z
  .object({
    schemaVersion: z.literal(IMPROMPTU_SCHEMA_VERSION),
    rubricVersion: z.literal(IMPROMPTU_RUBRIC_VERSION),
    // Preserve historical reports and record the actual audio/text provider model.
    model: z.string().min(1).max(200),
    attemptId: z.string().uuid(),
    promptId: z.string().min(1).max(120),
    languageCode: z.string().min(2).max(12),
    learnerProfile: z.string().min(1).max(40),
    headline: z.string().min(1).max(140),
    keepDoing: ReportObservationSchema,
    tryNext: ReportObservationSchema,
    message: z
      .object({
        relevance: ReportDimensionSchema,
        structure: ReportDimensionSchema,
      })
      .strict(),
    language: z
      .object({
        grammar: ReportDimensionSchema,
        vocabulary: ReportDimensionSchema,
      })
      .strict(),
    delivery: DeliveryMetricsSchema,
    preciseWording: z.array(z.string().min(1).max(100)).max(3),
    sharperVersion: z.string().min(1).max(900),
    transcript: z
      .object({
        text: z.string().min(1).max(12_000),
        words: z.array(TranscriptWordSchema).max(2_500),
      })
      .strict(),
    pronunciation: z
      .object({
        status: z.enum(["disabled", "unavailable"]),
      })
      .strict(),
  })
  .strict();

export type ImpromptuReport = z.infer<typeof ImpromptuReportSchema>;

export const PronunciationBandSchema = z.enum(["clear", "mostly_clear", "needs_practice"]);

const PronunciationSoundSchema = z
  .object({
    text: z.string().min(1).max(24),
    accuracyScore: z.number().min(0).max(100),
  })
  .strict();

const PronunciationWordSchema = z
  .object({
    word: z.string().min(1).max(48),
    accuracyScore: z.number().min(0).max(100),
    errorType: z
      .enum(["mispronunciation", "unexpected_break", "missing_break", "monotone"])
      .nullable(),
    syllables: z.array(PronunciationSoundSchema).max(8),
    phonemes: z.array(PronunciationSoundSchema).max(12),
  })
  .strict();

export const ImpromptuPronunciationResultSchema = z.discriminatedUnion("status", [
  z
    .object({
      version: z.literal(IMPROMPTU_PRONUNCIATION_VERSION),
      attemptId: z.string().uuid(),
      locale: z.string().min(2).max(12),
      status: z.literal("available"),
      pronunciationScore: z.number().min(0).max(100),
      accuracyScore: z.number().min(0).max(100),
      fluencyScore: z.number().min(0).max(100),
      prosodyScore: z.number().min(0).max(100).nullable(),
      accuracyBand: PronunciationBandSchema,
      fluencyBand: PronunciationBandSchema,
      wordsToReview: z.array(PronunciationWordSchema).max(3),
    })
    .strict(),
  z
    .object({
      version: z.literal(IMPROMPTU_PRONUNCIATION_VERSION),
      attemptId: z.string().uuid(),
      locale: z.string().min(2).max(12),
      status: z.literal("unavailable"),
      reason: z.enum(["disabled", "unsupported", "unavailable"]),
    })
    .strict(),
]);

export type ImpromptuPronunciationResult = z.infer<typeof ImpromptuPronunciationResultSchema>;

export const ImpromptuAttemptResultSchema = z
  .object({
    report: ImpromptuReportSchema,
    pronunciation: ImpromptuPronunciationResultSchema.nullable(),
  })
  .strict();

export type ImpromptuAttemptResult = z.infer<typeof ImpromptuAttemptResultSchema>;

export const ImpromptuAnalyzeErrorSchema = z
  .object({
    error: z.string(),
    code: z.enum([
      "IMPROMPTU_DISABLED",
      "INVALID_REQUEST",
      "RECORDING_TOO_LARGE",
      "RECORDING_TOO_SHORT",
      /** Attempts arrived too quickly. Waiting is enough to clear it. */
      "RATE_LIMITED",
      /** No practice attempts left. Waiting will not help; signing up will. */
      "PRACTICE_QUOTA_REACHED",
      /** Our own usage gate could not answer, so nothing has been charged. */
      "PRACTICE_UNAVAILABLE",
      "TRANSCRIPTION_UNAVAILABLE",
      "ANALYSIS_UNAVAILABLE",
    ]),
    /**
     * True when the learner can send the recording again. Retries must reuse
     * `attemptId` so the practice quota is not charged twice for one attempt.
     *
     * `RATE_LIMITED` and `PRACTICE_QUOTA_REACHED` are both 429s and differ only
     * here: a burst clears on its own, an exhausted quota does not. They were one
     * code until it put "wait a moment and try again" above a screen whose only
     * button was "Back to setup".
     */
    retryable: z.boolean(),
    attemptId: z.string().uuid().optional(),
    /**
     * How long until a retry can succeed, mirroring the `Retry-After` header.
     * Sent only when the wait is known, so the interface can hold the retry
     * button instead of offering one that fails the moment it is pressed.
     */
    retryAfterSeconds: z.number().int().nonnegative().optional(),
  })
  .strict();

export type ImpromptuAnalyzeError = z.infer<typeof ImpromptuAnalyzeErrorSchema>;
